interface TruncationNoticeProps {
  /** Number of records currently loaded in the client. */
  shown: number
  /** True total count of records on the server (from the list endpoint's `total`). */
  total: number
  /** Optional handler to fetch the full set. When omitted, no "Show all" button renders. */
  onShowAll?: () => void
  /** Disables the button + shows a loading label while the full set is being fetched. */
  loading?: boolean
}

/**
 * Surfaces the silent-truncation failure mode on client-side-paginated list pages.
 *
 * List pages fetch a large-but-capped batch (default take=1000) and paginate/search
 * client-side. Below the cap this is invisible and correct; above it, records beyond
 * the cap were silently dropped — the user had no signal. This renders a clear notice
 * (and an optional "Show all" escape hatch) only when `shown < total`.
 *
 * Assumes the loaded feed is ordered most-recent-first (all current callers
 * `orderBy createdAt desc`), which is what makes the "most recent" copy accurate.
 *
 * See: brain/Technical/List-Pagination-Pattern.md, brain/Decisions/2026-06-03-list-pagination-hotfix.md
 */
export function TruncationNotice({ shown, total, onShowAll, loading = false }: TruncationNoticeProps) {
  if (shown >= total) return null

  return (
    <div className="trunc-notice" role="status">
      <svg className="trunc-notice-icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4M12 8h.01" />
      </svg>
      <span className="trunc-notice-text">
        Showing the {shown.toLocaleString()} most recent of {total.toLocaleString()}. Older records aren’t loaded, so search and filters only cover what’s shown.
      </span>
      {onShowAll && (
        <button type="button" className="trunc-notice-btn" onClick={onShowAll} disabled={loading}>
          {loading ? 'Loading…' : `Show all ${total.toLocaleString()}`}
        </button>
      )}
    </div>
  )
}
